import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Snowflake, Clock, Leaf, ArrowRight, CheckCircle, ArrowDown, Thermometer, Droplets, Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import UnifiedContactModal from '../components/UnifiedContactModal';

const CherryHydrocoolerPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Cherry Hydrocoolers for Cold Chain Management"
        description="Protect your cherry cold chain with portable and stationary hydrocoolers. Rapid, gentle cooling to maintain fruit firmness and quality."
        canonicalPath="/cherry-hydrocoolers"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/Cherry-M.jpg"
            alt="Fresh cherries ready for hydrocooling"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/80 via-pink-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🍒</span>
              <span>Premium Cold Chain Solutions</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Cherry Hydrocoolers
              <span className="block text-pink-300">for Premium Cold Chain Management</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Better Cooling, Stronger Cold Chain
            </p>

            <motion.button
              onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-pink-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-pink-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
            >
              <span>Learn More</span>
              <ArrowDown size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-pink-50 to-red-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-pink-100 text-pink-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🍒</span>
              <span>Introduction</span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-pink-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                Cherries are one of the most temperature-sensitive fruits in the fresh produce industry. From orchard to packhouse to shipping container, proper cold chain management is critical to protecting fruit quality, reducing waste, and maintaining shelf life.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                A well-designed hydrocooler is the first and most important step in your cherry cold chain. Rapid, gentle cooling removes field heat right after harvest, helping growers and packers deliver firm, high-quality cherries that arrive in-market fresh — and sell for premium prices.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                For a broader overview of all hydrocooler configurations we offer, visit our main{' '}
                <Link to="/hydrocoolers" className="text-pink-600 hover:text-pink-700 font-medium underline">
                  Hydrocoolers
                </Link>{' '}
                page.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Rapid Hydrocooling Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-red-500 rounded-lg flex items-center justify-center">
                <Snowflake className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Why Rapid Hydrocooling Protects Your Cold Chain
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-xl p-6 md:p-8 border border-pink-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-pink-500 pl-6">
                "Rapid removal of field heat is essential to maintain stem freshness, fruit firmness, and minimize decay."
              </blockquote>
              
              <p className="text-gray-700 mb-6">
                According to the Northwest Cherry Growers' Postharvest Manual, hydrocooling cherries immediately after harvest:
              </p>
              
              <div className="space-y-4">
                {[
                  'Stops fruit respiration rates from spiking',
                  'Maintains stem freshness and fruit firmness',
                  'Reduces bruising and mold risk during storage',
                  'Keeps your entire cold chain more stable from orchard to retailer'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-red-50 rounded-lg p-4 mt-6 border border-red-200">
                <p className="text-red-700 font-medium">
                  Every hour that field heat lingers weakens your cold chain — and your packout quality.
                </p>
              </div>

              {/* Time and Temperature Targets for Cherries */}
              <div className="bg-blue-50 rounded-lg p-6 mt-6 border border-blue-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Time and Temperature Targets for Cherries
                </h3>
                <p className="text-gray-700 mb-4">
                  While every operation is different, practical cherry cold chain guidelines generally include:
                </p>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Removing field heat within 2–4 hours of harvest wherever possible</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Cooling pulp temperature below about 41°F / 5°C within that window</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Finishing cooling toward 32–34°F / 0–1°C before long storage or export</span>
                  </div>
                </div>
                <p className="text-gray-700 mt-4">
                  Hitting these targets in the first few hours after harvest often has more impact on cherry quality than small adjustments made later in storage.
                </p>
              </div>
            </div>
          </motion.div>

          {/* How CTC Hydrocoolers Fit Into Cold Chain */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <Thermometer className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                How CTC Waterworks Hydrocoolers Fit Into the Cold Chain
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <p className="text-gray-700 mb-6">
                Our portable and stationary cherry hydrocoolers are designed with real grower and packer needs in mind:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-white rounded-lg p-6 shadow-md border border-blue-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Portable Hydrocoolers</h3>
                  <p className="text-gray-700 mb-4">
                    Bring cold chain cooling right into the orchard. Set up near picking crews to pre-cool bins before transport. Portable hydrocoolers can be built as tunnel-style conveyor units or batch-style zoned units, depending on your volume and layout.
                  </p>
                  <img
                    src="/Portable.jpg"
                    alt="Portable cherry hydrocooler in orchard setting"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md border border-blue-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Stationary Hydrocoolers</h3>
                  <p className="text-gray-700 mb-4">
                    High-capacity tunnels sized for packhouse lines, giving you consistent cooling throughput for multiple bins per hour. The same tunnel and batch designs can be installed as stationary packhouse hydrocoolers when portability is not required, integrated into existing receiving and packing lines.
                  </p>
                  <img
                    src="/Tunnel.jpg"
                    alt="Stationary tunnel hydrocooler for cherry packing facility"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              </div>
              
              <p className="text-gray-700 mb-6">
                Our systems circulate clean, chilled water through bulk bins, totes, or RPC's, dropping fruit pulp temperature from 80°F to 34°F in minutes — so cherries enter cold storage and shipping lines at the ideal temperature for the next cold chain stage.
              </p>
              
              <div className="bg-blue-100 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center space-x-3">
                  <Droplets className="text-blue-600 flex-shrink-0" size={24} />
                  <p className="text-blue-800 font-medium">
                    Water conducts heat 25 times faster than air, making hydrocooling the most efficient method for removing field heat from cherries.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Where Portable Hydrocooling Fits in Cherry Operations */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Thermometer className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Where Portable Hydrocooling Fits in Cherry Operations
              </h2>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 md:p-8 border border-cyan-200">
              <p className="text-gray-700 mb-6">
                Portable hydrocoolers let you move the first stage of cooling closer to where cherries are picked, so you can collapse the gap between harvest and cold water. Common use cases include:
              </p>

              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-md border border-blue-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Remote ranches and long hauls</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Place portable hydrocoolers at ranches or field collection centers</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Cool fruit from around 80°F / 27°C down into the 30s°F / low single digits °C before transport</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Reduce the cooling load and risk when fruit finally reaches the plant</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md border border-blue-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Hot midday harvests</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Use portable hydrocoolers at shaded yards to strip out field heat from bins picked during the hottest part of the day</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Protect stem and fruit quality when vapor pressure deficit and dehydration risk are highest</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md border border-blue-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Export and long storage programs</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Use portable hydrocooling as the first deep cooling step so export fruit hits storage just above its freezing point quickly</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Help extend shelf life and reduce rejections at distant or premium markets</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md border border-blue-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Mixed varieties and special lots</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Use batch-style portable hydrocoolers with multiple zones to handle small, high-value lots without disrupting main tunnels</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Offer tailored cooling regimes for organic programs, trial blocks, or key customers</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Protect Your Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Shield className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Protect Your Brand and Reduce Shrink
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 md:p-8 border border-green-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-green-500 pl-6">
                "Temperature control is the single most important factor in maintaining cherry quality from harvest to retail."
              </blockquote>
              
              <p className="text-gray-700 mb-6">
                According to Washington State University Extension, the longer field heat stays in your fruit, the higher your shrink losses down the supply chain.
              </p>
              
              <div className="bg-white rounded-lg p-6 shadow-md border border-green-100 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Growers and packers who manage their cold chain from the first mile see:</h3>
                <div className="space-y-3">
                  {[
                    'Less soft fruit rejection',
                    'Better stem color retention',
                    'Reduced mold issues in transit',
                    'Stronger reputation with buyers and exporters'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">Without Proper Hydrocooling:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-red-600 text-xs">✕</span>
                      </div>
                      <span className="text-red-700">Internal fruit temperatures stay high</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-red-600 text-xs">✕</span>
                      </div>
                      <span className="text-red-700">Accelerated mold growth and softening</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                        <span className="text-red-600 text-xs">✕</span>
                      </div>
                      <span className="text-red-700">Shorter shelf life = lost profit</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">With CTC Hydrocooling:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-green-700">Rapid temperature reduction</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-green-700">Preserved fruit quality and firmness</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-green-700">Extended shelf life and market reach</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Water Quality Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Droplets className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Water Quality and Food Safety
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 md:p-8 border border-cyan-200">
              <p className="text-gray-700 mb-6">
                Hydrocooling is only as effective as the water quality you maintain. CTC Waterworks can help you integrate proper filtration and pH management into your system design — keeping your hydrocooler clean and safe as part of your larger postharvest cold chain program.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-5 shadow-md border border-blue-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Filtration Systems</h3>
                  <p className="text-gray-700 text-sm">
                    Keep recirculating water clean and free of debris with our integrated filtration solutions.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-md border border-blue-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Sanitation Options</h3>
                  <p className="text-gray-700 text-sm">
                    Maintain food safety with compatible sanitation systems for your hydrocooler water.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-md border border-blue-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Temperature Control</h3>
                  <p className="text-gray-700 text-sm">
                    Maintain precise water temperature for optimal cooling efficiency and produce quality.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Why Choose CTC Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <Zap className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Why Growers Choose CTC Waterworks
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <p className="text-gray-700 mb-6">
                For nearly 40 years, CTC Waterworks has helped Northwest cherry growers and packers strengthen their cold chain management with durable, efficient hydrocooling solutions. We build every system for:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-5 shadow-md border border-purple-100">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Simple Operation</h3>
                      <p className="text-gray-700 text-sm">
                        Easy to use in the field or packhouse with minimal training required.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-5 shadow-md border border-purple-100">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Easy Maintenance</h3>
                      <p className="text-gray-700 text-sm">
                        Designed for simple cleaning and maintenance during busy harvest seasons.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-5 shadow-md border border-purple-100">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Reliable Throughput</h3>
                      <p className="text-gray-700 text-sm">
                        Consistent cooling capacity even during peak harvest periods.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-5 shadow-md border border-purple-100">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Technical Support</h3>
                      <p className="text-gray-700 text-sm">
                        Expert assistance available when you need it most during harvest.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Trusted By Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-lg flex items-center justify-center">
                <Shield className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Trusted by Leading PNW Cherry Growers
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-6 md:p-8 border border-yellow-200">
              <p className="text-gray-700 mb-6">
                For decades, the region's top cherry producers have relied on CTC Waterworks hydrocoolers to maximize quality and shelf life.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <motion.a
                  href="https://washfruitgrowers.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
                >
                  <img
                    src="/dealer-logos/washington-fruit.png"
                    alt="Washington Fruit"
                    className="max-h-full max-w-full object-contain"
                    loading="eager"
                    onError={(e) => {
                      e.currentTarget.parentElement.innerHTML = '<div class="font-bold text-lg text-red-600">WASHINGTON FRUIT</div>';
                    }}
                  />
                </motion.a>

                <motion.a
                  href="https://rainierfruit.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
                >
                  <img
                    src="/dealer-logos/rainier-fruit.png"
                    alt="Rainier Fruit"
                    className="max-h-full max-w-full object-contain"
                    loading="eager"
                    onError={(e) => {
                      e.currentTarget.parentElement.innerHTML = '<div class="font-bold text-lg text-red-600">RAINIER FRUIT</div>';
                    }}
                  />
                </motion.a>

                <motion.a
                  href="https://allanbrosfruit.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
                >
                  <img
                    src="/dealer-logos/allan-bros.png"
                    alt="Allan Brothers"
                    className="max-h-full max-w-full object-contain"
                    loading="eager"
                    onError={(e) => {
                      e.currentTarget.parentElement.innerHTML = '<div class="font-bold text-lg text-red-600">ALLAN BROTHERS</div>';
                    }}
                  />
                </motion.a>

                <motion.a
                  href="https://gebbersfarms.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
                >
                  <img
                    src="/dealer-logos/gebbers-farms.png"
                    alt="Gebbers Farms"
                    className="max-h-full max-w-full object-contain"
                    loading="eager"
                  />
                </motion.a>
              </div>
            </div>
          </motion.div>
          
          {/* Refrigeration Partners Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Snowflake className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Refrigeration Partners
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <p className="text-gray-700 mb-6">
                We work closely with these trusted refrigeration companies, but are happy to collaborate with any refrigeration provider you prefer.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <motion.a
                  href="https://versatilerefrigeration.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-xl p-6 shadow-md border border-blue-200 flex items-center justify-center h-24"
                >
                  <img
                    src="/dealer-logos/versatile-refrigeration.jpg"
                    alt="Versatile Refrigeration"
                    className="max-h-full max-w-full object-contain"
                    loading="eager"
                  />
                </motion.a>

                <motion.a
                  href="https://www.cwrcentral.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-xl p-6 shadow-md border border-blue-200 flex items-center justify-center h-24"
                >
                  <img
                    src="/dealer-logos/cwr.png"
                    alt="Central Washington Refrigeration"
                    className="max-h-full max-w-full object-contain"
                    loading="eager"
                  />
                </motion.a>
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-red-900 via-pink-800 to-black text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
              Ready to Strengthen Your Cherry Cold Chain?
            </h2>
            <p className="text-lg md:text-xl mb-6 md:mb-8 opacity-90">
              A reliable cold chain starts with removing field heat fast — and the right hydrocooler makes that possible. If you're ready to protect your cherries and your brand, let's talk about a custom portable or stationary hydrocooler for your operation.
            </p>
            
            <motion.button
              onClick={() => setIsContactModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-pink-500 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold hover:bg-pink-600 transition-colors inline-flex items-center justify-center space-x-2 shadow-lg"
            >
              <span>Get Your Hydrocooler Quote</span>
              <ArrowRight size={20} />
            </motion.button>

            <div className="mt-8 text-sm opacity-80">
              <p>Trusted by Northwest cherry growers for nearly 40 years</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Modal */}
      <UnifiedContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)}
        productType="hydrocoolers"
        formSource="product_quote"
        title="Request Cherry Hydrocooler Information"
        subtitle="Let us help you design the perfect solution for your operation"
      />

      <Footer />
    </div>
  );
};

export default CherryHydrocoolerPage;